import React from 'react';
import { IAuditoriaPlIntegridadeResultado } from '../interfaces/IAuditoriaPlIntegridade';

interface Props {
  resultado: IAuditoriaPlIntegridadeResultado;
  onVerDetalhe: () => void;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const TipoBadge: React.FC<{ tipo: string }> = ({ tipo }) => {
  if (tipo === 'CONTRAPARTIDA_CAIXA')
    return <span className='badge badge-light-danger'>Contrapartida Caixa/Banco</span>;
  if (tipo === 'CONTRAPARTIDA_CAIXA_PENDENTE_VALIDACAO')
    return <span className='badge badge-light-info'>Pendente de Validação Documental</span>;
  return <span className='badge badge-light-warning'>Dia Não Útil</span>;
};

const AuditoriaResumoPlIntegridade: React.FC<Props> = ({ resultado, onVerDetalhe }) => {
  if (!resultado || resultado.itens.length === 0) return null;

  const top5 = [...resultado.itens]
    .sort((a, b) => b.passivoTributario - a.passivoTributario)
    .slice(0, 5);

  const statusCls =
    resultado.status === 'ERRO'   ? 'border-danger' :
    resultado.status === 'ALERTA' ? 'border-warning' :
    resultado.status === 'AVISO'  ? 'border-info' :
                                    'border-success';

  return (
    <div className={`card card-custom card-stretch mb-5 border-top border-4 ${statusCls}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-5 mb-1'>
            Integridade do Patrimônio Líquido — Lançamentos Atípicos (PL)
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            {resultado.totalErros} erro(s) contrapartida Caixa/Banco · {resultado.totalAvisos} aviso(s) pendente(s) de validação · {resultado.totalAlertas} alerta(s) dia não útil
            {resultado.passivoTributarioTotal > 0 && (
              <> · Passivo: <strong className='text-danger'>{formatCurrency(resultado.passivoTributarioTotal)}</strong></>
            )}
          </span>
        </h3>
        <div className='card-toolbar'>
          <button className='btn btn-sm btn-light-primary' onClick={onVerDetalhe}>
            Ver Detalhes
          </button>
        </div>
      </div>

      <div className='card-body pt-3'>
        <div className='row g-3 mb-4'>
          <div className='col-md-2'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Erros (Caixa/Banco)</div>
              <div className='fw-bolder fs-3 text-danger'>{resultado.totalErros}</div>
            </div>
          </div>
          <div className='col-md-2'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Avisos (Pendente Validação)</div>
              <div className='fw-bolder fs-3 text-info'>{resultado.totalAvisos}</div>
            </div>
          </div>
          <div className='col-md-2'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Alertas (Dia N. Útil)</div>
              <div className='fw-bolder fs-3 text-warning'>{resultado.totalAlertas}</div>
            </div>
          </div>
          <div className='col-md-3'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Tributo Estimado</div>
              <div className='fw-bolder fs-5 text-danger'>
                {formatCurrency(resultado.itens.reduce((s, i) => s + (i.tributoDevido || 0), 0))}
              </div>
            </div>
          </div>
          <div className='col-md-3'>
            <div className='card border-0 bg-light p-3 text-center'>
              <div className='text-muted fs-8'>Passivo Total</div>
              <div className='fw-bolder fs-5 text-danger'>{formatCurrency(resultado.passivoTributarioTotal)}</div>
            </div>
          </div>
        </div>

        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
            <thead>
              <tr className='fw-bolder text-muted'>
                <th>Empresa</th>
                <th className='text-center'>Data</th>
                <th>Conta PL</th>
                <th className='text-center'>Tipo</th>
                <th className='text-end'>Valor</th>
                <th className='text-end'>Passivo</th>
                <th className='text-center'>Ocorrência</th>
              </tr>
            </thead>
            <tbody>
              {top5.map((item, i) => (
                <tr key={i}>
                  <td>
                    <span className='fw-bold'>{item.empresaNome}</span>
                    <br /><span className='text-muted fs-8'>{item.cnpj}</span>
                  </td>
                  <td className='text-center fw-bold'>{item.dataLancamento}</td>
                  <td>
                    <span className='fw-semibold'>{item.descricaoContaPl}</span>
                    <br /><span className='text-muted fs-8'>{item.contaPl}</span>
                  </td>
                  <td className='text-center'>
                    <span className={`badge ${item.tipoLancamento === 'C' ? 'badge-light-success' : 'badge-light-danger'}`}>
                      {item.tipoLancamento === 'C' ? 'Crédito' : 'Débito'}
                    </span>
                  </td>
                  <td className='text-end fw-bold'>{formatCurrency(item.valor)}</td>
                  <td className='text-end fw-bold text-danger'>{formatCurrency(item.passivoTributario)}</td>
                  <td className='text-center'><TipoBadge tipo={item.tipoOcorrencia} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {resultado.itens.length > 5 && (
          <div className='text-center mt-2'>
            <button className='btn btn-sm btn-light' onClick={onVerDetalhe}>
              Ver todos os {resultado.itens.length} lançamentos
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditoriaResumoPlIntegridade;
